import React, { KeyboardEvent } from 'react'
import { SpaceProps } from 'styled-system'
import { LabelVariants } from '@zuto/atoms/src/Form/FormLabel'
import Box from '@mui/material/Box'
import MuiTextField from '@zuto/atoms/src/Mui/TextField/MuiTextField'
import MuiErrorMessage from '@zuto/atoms/src/Mui/ErrorMessage/MuiErrorMessage'

export interface DurationProps extends
  Omit<React.InputHTMLAttributes<HTMLDivElement>, 'defaultValue'>,
  SpaceProps {
  /** Content of the error for the field */
  error?: {
    message?: string,
    fields: string[]
  }
  onChange?: React.ChangeEventHandler<HTMLInputElement>
  onKeyPress?: (event: KeyboardEvent<HTMLDivElement>) => void
  defaultValue?: Record<string, string | undefined>
  labelVariant?: LabelVariants
  onKeyPressHandler?: React.KeyboardEventHandler<HTMLInputElement>
}

const MuiDuration = ({
  error,
  onChange,
  onKeyPress,
  defaultValue,
  onKeyPressHandler
}: DurationProps): JSX.Element => {
  const styles = {
    display: 'flex',
    alignItems: 'flex-end',
    flexWrap: 'wrap'
  }

  const inputRefs: (HTMLInputElement | null)[] = []

  const stopNegativeValues = (value: string) => {
    const valueAsInt = parseInt(value, 10)
    return Math.abs(valueAsInt) >= 0 ? Math.abs(valueAsInt).toString() : value
  }

  const sliceToMaxLength = (value: string, maxLength: string | null) => {
    if (!maxLength) {
      return value
    }

    return value.slice(0, parseInt(maxLength, 10))
  }

  const eventHandler = (input: HTMLInputElement): string => {
    let newValue = input.value.replace(/[^0-9]/g, '')
    newValue = stopNegativeValues(newValue)
    newValue = sliceToMaxLength(newValue, input.getAttribute('maxLength'))

    return newValue
  }

  const autoFocusNext = (input: HTMLInputElement) => {
    const maxLength = input.getAttribute('maxlength')
    if (maxLength && input.value.length >= parseInt(maxLength, 10)) {
      const currentInputIndex = inputRefs.indexOf(input)
      if (currentInputIndex === inputRefs.length - 1) {
        return
      }
      const nextInput = inputRefs[currentInputIndex + 1]

      if (nextInput) {
        nextInput.focus()
      }
    }
  }

  const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.currentTarget.value = eventHandler(e.currentTarget)
    autoFocusNext(e.currentTarget)
    if (onChange) {
      onChange(e)
    }
  }

  const onBlurHandler = (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.value = eventHandler(e.currentTarget)
  }

  return (
    <Box
      sx={{ ...styles }}
      data-hj-suppress=""
      onKeyPress={onKeyPressHandler}
    >
      <MuiTextField
        aria-label="label-years"
        defaultValue={defaultValue?.years as string}
        id="years"
        inputMaxLength={2}
        inputSize="w50"
        inputType="number"
        inputVariant="outlined"
        label="Years"
        min="0"
        name="years"
        onBlurHandler={onBlurHandler}
        onChangeHandler={onChangeHandler}
        onKeyPressHandler={onKeyPress}
        pattern="[0-9]*"
        refHandler={(r: HTMLInputElement) => inputRefs.push(r)}
        spacingRight
        testId="years-input"
      />
      <MuiTextField
        aria-label="label-months"
        defaultValue={defaultValue?.months as string}
        isError={!!error?.message}
        id="months"
        inputMaxLength={2}
        inputSize="w50"
        inputType="number"
        inputVariant="outlined"
        label="Months"
        max="11"
        min="0"
        name="months"
        onBlurHandler={onBlurHandler}
        onChangeHandler={onChangeHandler}
        onKeyPressHandler={onKeyPress}
        pattern="[0-9]*"
        refHandler={(r: HTMLInputElement) => inputRefs.push(r)}
        testId="months-input"
      />
      {error?.message && (
        <MuiErrorMessage error={error.message} />
      )}
    </Box>
  )
}

export default MuiDuration
